import React, {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter, Redirect} from 'react-router-dom';
import {Spinner, Button} from 'react-bootstrap';
import {push} from 'connected-react-router';
import {useTranslation} from 'react-i18next';
import CommonMetaTag from './common-meta-tag';
import PendingInvitationAlerts from './pending-invitation-alerts';
import PlayerHighLight from './player-highlight';
import PlayerInsufficientFunds from './player-insufficient-funds';
import PlayerNewEventSubscription from './player-new-event-subscription';
import PlayerNewSubscription from './player-new-subscription';
import PurchasedVideoHomeCard from './purchased-video-home-card';
import SpecialEventHomeCard from './special-event-home-card';
import Configuration from '../config';
import {addVideoToPlayerArea, getBookings, getLiveVideos, getPartners, getVideo, getVideoStreaming, partnerSelected, getPartnerFields, getPartnerServices, getPartnerEvents, filterBookingFieldId, filterBookableFields, getSlots, postUsageData, permanentVideoSelected, showVideoHighlightModal, getRtvPartnerVideos, getRtvVideos, rtvChannelSelected, showPlayerNewEventSubscriptionModal, showPlayerNewSubscriptionModal, specialEventSelected} from '../actions/player';
import {showMessage} from '../actions/messages';
import {getPublicOpenMatches} from '../api';
import {openVideoPlayer} from '../utils/video-player-preload';
import {FaBolt, FaCalendarAlt, FaCalendarPlus, FaChevronRight, FaCompass, FaHome, FaList, FaPlayCircle, FaStar, FaTv, FaUsers, FaVideo} from 'react-icons/fa';
import replayerlogoBianco from '../images/replayer_logo_bianco_40x32px.png';
import {SPORTS, subscriptionStatuses} from '../constants';
import '../css/home-redesign.css';

const SPORT_LABELS = {
  tennis: 'SPORT_TENNIS',
  padel: 'SPORT_PADEL',
  pickleball: 'SPORT_PICKLEBALL'
};

const normalizeSport = value => String(value || '')
  .toLowerCase()
  .trim()
  .replace(/^sport[-_\s]*/, '')
  .replace(/^replayer[-_\s]*/, '');

const sportDisplayName = (sport, t) => {
  const normalized = normalizeSport(sport);
  return t(SPORT_LABELS[normalized] || 'SPORT') || normalized || '';
};

const formatDateLong = (value, locale) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, {weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'});
};

const formatTime = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
};

const partnerLogoUrl = partner => {
  const logo = partner && partner.storeImage && partner.storeImage.length > 0 ? partner.storeImage[0] : null;
  if (!logo || !partner) return null;
  return `${Configuration.apiEndPoint}/images/${partner.uid}?load=${logo}`;
};

const bookingPartnerLogoUrl = (booking, partners = []) => {
  if (!booking || !booking.partnerId) return null;
  const partner = partners.find(p => Number(p.uid) === Number(booking.partnerId));
  return partnerLogoUrl(partner);
};

const openMatchLogoUrl = match => {
  const logo = match && (match.partnerLogoUrl || match.logoUrl || match.partnerLogo);
  if (!logo) return null;
  if (/^https?:\/\//.test(logo) || logo.startsWith('/')) return logo;
  const partnerId = match.partnerId || match.partnerUid;
  return partnerId ? `${Configuration.apiEndPoint}/images/${partnerId}?load=${encodeURIComponent(logo)}` : null;
};

const partnerPublicSlug = partner => partner && (partner.publicSlug || partner.public_slug || null);

const canBookPartner = partner => Boolean(partner && ((partner.bookableFields && !partner.isSpecialist) || (partner.bookableServices && partner.isSpecialist)));

const matchPublicId = match => String(match && (match.publicId || match.public_id || match.id || '')).trim();

const nextMatch = matches => {
  const now = Date.now();
  return (matches || [])
    .filter(match => {
      const start = match.startDatetime ? new Date(match.startDatetime).getTime() : 0;
      const status = String(match.status || '').toLowerCase();
      return start > now && ['open', 'full'].includes(status) && Number(match.playersJoined || 0) < Number(match.playersRequired || 0);
    })
    .sort((a, b) => new Date(a.startDatetime) - new Date(b.startDatetime));
};

const HomeRedesign = ({
  location,
  token,
  profile,
  partners,
  favouritePartners,
  favouriteRtvChannels,
  bookings,
  video,
  videoStreaming,
  liveVideos,
  events,
  subscriptions,
  selectedPermanentVideo,
  loadPartners,
  loadBookings,
  loadVideo,
  loadVideoStreaming,
  loadLiveVideos,
  showPartner,
  showCalendar,
  goto,
  sendUsageData,
  createPurchasedHighlight,
  copyPurchasedVideo,
  openSpecialEventAccess
}) => {
  const {t, i18n} = useTranslation();
  const [openMatches, setOpenMatches] = useState([]);
  const [openMatchesLoading, setOpenMatchesLoading] = useState(false);

  const locale = i18n.language || t('LOCALE_CODE', 'it-IT');

  useEffect(() => {
    if (!token) return;
    loadPartners();
    loadBookings();
    loadVideo();
    loadVideoStreaming();
    loadLiveVideos();
  }, [loadBookings, loadLiveVideos, loadPartners, loadVideo, loadVideoStreaming, token]);

  useEffect(() => {
    if (!location || location.hash !== '#special-events') return undefined;
    const scrollTimer = window.setTimeout(() => {
      const section = document.getElementById('special-events');
      if (section) section.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, 0);
    return () => window.clearTimeout(scrollTimer);
  }, [events.length, location]);

  const hasPendingPurchasedVideos = videoStreaming.length > 0;
  useEffect(() => {
    if (!token || !hasPendingPurchasedVideos) return undefined;
    const intervalId = window.setInterval(() => {
      if (document.hidden) return;
      loadVideoStreaming();
      loadVideo();
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [hasPendingPurchasedVideos, loadVideo, loadVideoStreaming, token]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!token) return;
      setOpenMatchesLoading(true);
      const res = await getPublicOpenMatches({limit: 8});
      if (mounted && res.status === 200) {
        setOpenMatches(res.json.openMatches || []);
      }
      if (mounted) setOpenMatchesLoading(false);
    };
    load();
    return () => {
      mounted = false;
    };
  }, [token]);

  const upcomingBookings = useMemo(() => (
    (bookings || [])
      .filter(booking => new Date(booking.startDatetime) > new Date())
      .sort((a, b) => new Date(a.startDatetime) - new Date(b.startDatetime))
      .slice(0, 4)
  ), [bookings]);

  const openMatchesPreview = useMemo(() => nextMatch(openMatches).slice(0, 6), [openMatches]);
  const liveVideosPreview = useMemo(() => (liveVideos || []).slice(0, 4), [liveVideos]);
  const purchasedVideosPreview = useMemo(() => {
    const bySlot = new Map();
    [...(videoStreaming || []), ...(video || [])].forEach(item => {
      const existing = bySlot.get(item.slotId);
      if (!existing || item.videoComplete) bySlot.set(item.slotId, item);
    });
    return [...bySlot.values()]
      .sort((a, b) => new Date(b.purchaseDate || 0) - new Date(a.purchaseDate || 0))
      .slice(0, 6);
  }, [video, videoStreaming]);
  const favoriteCentersPreview = useMemo(() => (favouritePartners || []).slice(0, 6), [favouritePartners]);
  const favoriteRtvPreview = useMemo(() => (favouriteRtvChannels || []).slice(0, 6), [favouriteRtvChannels]);

  const goToVideo = useCallback(v => () => {
    if (!v) return;
    if (v.token && v.validVideos) {
      sendUsageData(v.partnerId, v.slotId, Boolean(v.event || v.streaming));
      window.open(`${Configuration.krpanoPlayerUrl}?slotId=${v.slotId}&token=${v.token}`, '_blank');
      return;
    }
    goto('/player-live-videos');
  }, [goto, sendUsageData]);

  const playPurchasedVideo = useCallback(v => {
    if (!v || !v.playable || !v.token) return;
    sendUsageData(v.partnerId, v.slotId, true);
    openVideoPlayer(`${Configuration.krpanoPlayerUrl}?slotId=${v.slotId}&token=${v.token}`);
  }, [sendUsageData]);

  const openRtvChannel = useCallback((partner, channel) => () => {
    const slug = partnerPublicSlug(partner);
    if (slug) {
      window.location.href = `/club/${slug}?view=tv&channel_id=${channel.id}#club-matches`;
      return;
    }
    goto('/player-replayer-tv-home');
  }, [goto]);

  const openMatch = useCallback(match => () => {
    const publicId = matchPublicId(match);
    if (publicId) {
      goto(`/matches/${publicId}`)();
    }
  }, [goto]);

  const openLivePreview = useCallback(v => () => {
    if (!v) {
      goto('/player-live-videos')();
      return;
    }
    if (v.token && v.validVideos) {
      sendUsageData(v.partnerId, v.slotId, Boolean(v.event || v.streaming));
      window.open(`${Configuration.krpanoPlayerUrl}?slotId=${v.slotId}&token=${v.token}`, '_blank');
      return;
    }
    goto('/player-live-videos')();
  }, [goto, sendUsageData]);

  const openCenter = useCallback(partner => () => {
    if (!partner) return;
    showPartner(partner)();
  }, [showPartner]);

  if (!token) {
    return <Redirect to={`/login?next=${encodeURIComponent((location && location.pathname) || '/home-redesign')}`}/>;
  }

  const renderCardBadge = (title, subtitle, icon, className = '', onClick) => (
    <button type="button" className={`home-redesign-action-card ${className}`} onClick={onClick}>
      <div className="home-redesign-action-card__icon" aria-hidden="true">{icon}</div>
      <div className="home-redesign-action-card__copy">
        <strong>{title}</strong>
        <span>{subtitle}</span>
      </div>
    </button>
  );

  const sectionHeader = (number, title, description, action = null) => (
    <div className="home-redesign-section-head">
      <div className="home-redesign-section-head__copy">
        {number ? (
          <span className="home-redesign-section-number">
            <strong>{number}</strong>
          </span>
        ) : null}
        <h2 className="home-redesign-section-title">
          <span className="home-redesign-section-title__primary">{title.primary}</span>
          <span className="home-redesign-section-title__accent">{title.accent}</span>
        </h2>
        {description ? <p>{description}</p> : null}
      </div>
      {action}
    </div>
  );

  const rail = (children, sectionClass = '') => (
    <div className={`home-redesign-rail ${sectionClass}`}>{children}</div>
  );

  return (
    <Fragment>
      <CommonMetaTag/>
      <PendingInvitationAlerts/>
      <div className="home-redesign">
        <div className="home-redesign-debug-banner">
          <strong>{t('HOME_REDESIGN_ACTIVE')}</strong>
          <span>{t('HOME_REDESIGN_TITLE')}</span>
        </div>
          <div className="home-redesign__hero-bg" aria-hidden="true">
            <div className="home-redesign__hero-overlay"/>
          </div>
          <div className="home-redesign-shell">
            <div className="home-redesign-hero">
                <div className="home-redesign-hero__copy">
                  <span className="home-redesign-hero__eyebrow">{t('HOME_REDESIGN_EYEBROW')}</span>
                  <h1 className="home-redesign-hero__title">
                    <span className="home-redesign-section-title__primary">{t('HOME_REDESIGN_TITLE_PART_1')}</span>
                    <span className="home-redesign-section-title__accent">{t('HOME_REDESIGN_TITLE_PART_2')}</span>
                    <span className="home-redesign-section-title__primary">{t('HOME_REDESIGN_TITLE_PART_3')}</span>
                  </h1>
                  <p>{t('HOME_REDESIGN_SUBTITLE')}</p>
                  <div className="home-redesign-hero__actions">
                    <Button type="button" className="btn home-skywork-action home-skywork-action--video home-redesign-hero__action home-redesign-hero__action--watch" onClick={goto('/player-replayer-tv-home')}>
                      <FaPlayCircle aria-hidden="true"/>{t('HOME_REDESIGN_WATCH')}
                    </Button>
                    <Button type="button" className="btn home-skywork-action home-skywork-action--primary home-redesign-hero__action home-redesign-hero__action--book" onClick={goto('/player-show-sport-centers?bookable=true')}>
                      <FaCalendarPlus aria-hidden="true"/>{t('HOME_REDESIGN_BOOK')}
                    </Button>
                      <Button type="button" className="btn home-skywork-action home-skywork-action--open-match home-redesign-hero__action home-redesign-hero__action--play" onClick={goto('/player-open-matches')}>
                      <FaUsers aria-hidden="true"/>{t('HOME_REDESIGN_PLAY')}
                    </Button>
                  </div>
                </div>
                <div className="home-redesign-hero__cards">
                  {renderCardBadge(t('HOME_REDESIGN_WATCH'), t('HOME_REDESIGN_WATCH_DESC'), <FaVideo aria-hidden="true"/>, 'home-redesign-action-card--watch', goto('/player-replayer-tv-home'))}
                  {renderCardBadge(t('HOME_REDESIGN_BOOK'), t('HOME_REDESIGN_BOOK_DESC'), <FaCalendarAlt aria-hidden="true"/>, 'home-redesign-action-card--book', goto('/player-show-sport-centers?bookable=true'))}
                  {renderCardBadge(t('HOME_REDESIGN_PLAY'), t('HOME_REDESIGN_PLAY_DESC'), <FaUsers aria-hidden="true"/>, 'home-redesign-action-card--play', goto('/player-open-matches'))}
                </div>
              </div>

              <section className="home-redesign-section">
                {sectionHeader('01', {
                  primary: t('HOME_PURCHASED_VIDEO_TITLE_PRIMARY'),
                  accent: t('HOME_PURCHASED_VIDEO_TITLE_ACCENT')
                }, t('HOME_REDESIGN_STREAMING_DESC'))}
                {purchasedVideosPreview.length > 0 ? rail(purchasedVideosPreview.map(videoItem => (
                  <PurchasedVideoHomeCard
                    key={videoItem.slotId}
                    video={videoItem}
                    locale={locale}
                    t={t}
                    onPlay={playPurchasedVideo}
                    onCreateHighlight={createPurchasedHighlight}
                    onCopy={copyPurchasedVideo}
                  />
                ))) : (
                  <div className="home-redesign-empty-strip">
                    <FaVideo aria-hidden="true"/>
                    <p>{t('HOME_PURCHASED_VIDEO_EMPTY')}</p>
                  </div>
                )}
              </section>

              <section className="home-redesign-section">
                {sectionHeader('02', {
                  primary: t('HOME_REDESIGN_LIVE_TITLE_PRIMARY'),
                  accent: t('HOME_REDESIGN_LIVE_TITLE_ACCENT')
                }, t('HOME_REDESIGN_LIVE_DESC'), (
                  <div className="home-redesign-live-actions">
                    <Button
                      type="button"
                      className="btn home-skywork-action home-skywork-action--secondary home-redesign-section-link home-redesign-section-link--live"
                      onClick={openLivePreview(liveVideosPreview[0] || null)}
                    >
                      <FaVideo aria-hidden="true"/>{t('HOME_REDESIGN_OPEN_LIVE')}
                    </Button>
                    <Button type="button" className="btn home-skywork-action home-skywork-action--secondary home-redesign-section-link" onClick={goto('/player-live-videos')}>
                      {t('HOME_REDESIGN_VIEW_ALL')} <FaChevronRight aria-hidden="true"/>
                    </Button>
                  </div>
                ))}
                {liveVideosPreview.length > 0 ? rail(liveVideosPreview.map(v => {
                  const logo = v.rtvChannels && v.rtvChannels.length === 1 && v.rtvChannels[0].logoNames && v.rtvChannels[0].logoNames.length > 0
                    ? `${Configuration.apiEndPoint}/partners/rtv_channels/${v.rtvChannels[0].id}/images?load=${v.rtvChannels[0].logoNames[0]}`
                    : v.event ? replayerlogoBianco : replayerlogoBianco;
                  const sportIcon = SPORTS[v.sport] ? SPORTS[v.sport].icon : null;
                  const canOpen = v.token && v.validVideos;
                  return (
                    <article key={v.id} className="home-redesign-rail-card home-redesign-rail-card--live">
                      <button type="button" className="home-redesign-media" onClick={goToVideo(v)}>
                        <div className="home-redesign-media__image" style={v.partnerId && v.fieldImage ? {backgroundImage: `url(${Configuration.apiEndPoint}/partners/fields/${v.fieldId}/images?load=${v.fieldImage})`} : null}>
                          <span className="home-redesign-badge home-redesign-badge--live"><FaBolt aria-hidden="true"/> LIVE</span>
                          {sportIcon ? <span className="home-redesign-sport-icon"><img src={sportIcon} alt={t('ICON_SPORT')}/></span> : null}
                          <span className="home-redesign-media__logo"><img src={logo} alt="" aria-hidden="true"/></span>
                          <span className="home-redesign-media__overlay-text">{v.description ? v.description.substring(0, 56) : ''}</span>
                        </div>
                      </button>
                      <div className="home-redesign-rail-card__body">
                        <div className="home-redesign-rail-card__meta">
                          <strong>{v.description || t('WATCH_LIVE')}</strong>
                          <span>{v.fieldName}</span>
                        </div>
                        <div className="home-redesign-rail-card__details">
                          <span>{formatDateLong(v.startDatetime, locale)}</span>
                          <span>{formatTime(v.startDatetime)} - {formatTime(v.endDatetime)}</span>
                        </div>
                        <div className="home-redesign-rail-card__actions">
                          <Button type="button" className="btn home-skywork-action home-skywork-action--secondary" onClick={canOpen ? goToVideo(v) : goto('/player-live-videos')}>
                            {canOpen ? t('HOME_REDESIGN_WATCH_LIVE') : t('HOME_REDESIGN_OPEN_LIVE')}
                          </Button>
                        </div>
                      </div>
                    </article>
                  );
                })) : (
                  <div className="home-redesign-empty-strip">
                    <FaBolt aria-hidden="true"/>
                    <p>{t('HOME_REDESIGN_LIVE_EMPTY')}</p>
                  </div>
                )}
              </section>

              <section className="home-redesign-section">
                {sectionHeader('03', {
                  primary: t('HOME_REDESIGN_RTV_TITLE_PRIMARY'),
                  accent: t('HOME_REDESIGN_RTV_TITLE_ACCENT')
                }, t('HOME_REDESIGN_RTV_DESC'), (
                  <Button type="button" className="btn home-skywork-action home-skywork-action--secondary home-redesign-section-link" onClick={goto('/player-replayer-tv-home')}>
                    {t('HOME_REDESIGN_VIEW_ALL')} <FaChevronRight aria-hidden="true"/>
                  </Button>
                ))}
                {favoriteRtvPreview.length > 0 ? rail(favoriteRtvPreview.map(channel => {
                  const partner = partners.find(p => Number(p.uid) === Number(channel.uid));
                  const logo = channel.logoNames && channel.logoNames.length > 0
                    ? `${Configuration.apiEndPoint}/partners/rtv_channels/${channel.id}/images?load=${channel.logoNames[0]}`
                    : partnerLogoUrl(partner);
                  return (
                    <article key={`${channel.uid}-${channel.id}`} className="home-redesign-rail-card home-redesign-rail-card--rtv">
                      <button type="button" className="home-redesign-media" onClick={openRtvChannel(partner || {}, channel)}>
                        <div className="home-redesign-media__image home-redesign-media__image--rtv" style={logo ? {backgroundImage: `url(${logo})`} : null}>
                          <span className="home-redesign-badge home-redesign-badge--center">
                            REPLAYERTV
                          </span>
                          <span className="home-redesign-media__overlay-text">{channel.name}</span>
                        </div>
                      </button>
                      <div className="home-redesign-rail-card__body">
                        <div className="home-redesign-rail-card__meta">
                          <strong>{channel.name}</strong>
                          <span>{partner ? partner.name : t('REPLAYER_TV')}</span>
                        </div>
                        <div className="home-redesign-rail-card__details">
                          <span>{channel.type === 0 ? t('HOME_REDESIGN_RTV_CHANNEL') : t('HOME_REDESIGN_RTV_PARTNER')}</span>
                        </div>
                        <div className="home-redesign-rail-card__actions">
                          <Button type="button" className="btn home-skywork-action home-skywork-action--secondary" onClick={openRtvChannel(partner || {}, channel)}>
                            <FaTv aria-hidden="true"/>
                            <FaVideo aria-hidden="true"/>
                            {t('HOME_REDESIGN_OPEN_RTV_AND_STORE')}
                          </Button>
                        </div>
                      </div>
                    </article>
                  );
                })) : (
                  <div className="home-redesign-empty-strip">
                    <FaTv aria-hidden="true"/>
                    <p>{t('HOME_REDESIGN_RTV_EMPTY')}</p>
                    <div className="home-redesign-empty-strip__actions">
                      <Button type="button" className="btn home-skywork-action home-skywork-action--secondary" onClick={goto('/player-replayer-tv-home')}>
                        <FaTv aria-hidden="true"/>
                        <FaVideo aria-hidden="true"/>
                        {t('HOME_REDESIGN_OPEN_RTV_AND_STORE')}
                      </Button>
                    </div>
                  </div>
                )}
              </section>

              <section className="home-redesign-section">
                {sectionHeader('04', {
                  primary: t('HOME_REDESIGN_OPEN_MATCH_TITLE_PRIMARY'),
                  accent: t('HOME_REDESIGN_OPEN_MATCH_TITLE_ACCENT')
                }, t('HOME_REDESIGN_OPEN_MATCH_DESC'), (
                  <Button type="button" className="btn home-skywork-action home-skywork-action--secondary home-redesign-section-link" onClick={goto('/player-open-matches')}>
                    {t('HOME_REDESIGN_VIEW_ALL')} <FaChevronRight aria-hidden="true"/>
                  </Button>
                ))}
                <article className="home-redesign-feature-cta home-redesign-feature-cta--open-match home-redesign-feature-cta--match-wide">
                  <div className="home-redesign-feature-cta__icon"><FaUsers aria-hidden="true"/></div>
                  <div className="home-redesign-feature-cta__copy">
                    <strong>{t('HOME_REDESIGN_OPEN_MATCH_CTA_TITLE')}</strong>
                    <p>{t('HOME_REDESIGN_OPEN_MATCH_CTA_DESC')}</p>
                      <div className="home-redesign-feature-cta__actions">
                        <Button type="button" className="btn home-skywork-action home-skywork-action--open-match" onClick={goto('/open-matches/create')}>
                          <FaUsers aria-hidden="true"/>
                          {t('HOME_REDESIGN_CREATE_MATCH')}
                        </Button>
                        <Button type="button" className="btn home-skywork-action home-skywork-action--video" onClick={goto('/player-open-matches')}>
                          <FaList aria-hidden="true"/>
                          {t('HOME_REDESIGN_OPEN_MATCH_BROWSE')}
                        </Button>
                      </div>
                  </div>
                </article>
                {openMatchesLoading && openMatchesPreview.length === 0 ? (
                  <div className="home-redesign-empty-strip home-redesign-empty-strip--compact">
                    <Spinner animation="border" role="status" size="sm"/>
                    <p>{t('HOME_REDESIGN_OPEN_MATCH_LOADING')}</p>
                  </div>
                ) : null}
                <div className="home-redesign-rail home-redesign-rail--open-matches">
                  {openMatchesPreview.map(match => {
                    const logo = openMatchLogoUrl(match);
                    return (
                      <article key={matchPublicId(match)} className="home-redesign-rail-card home-redesign-rail-card--match">
                        <button type="button" className="home-redesign-media" onClick={openMatch(match)}>
                          <div className="home-redesign-media__image home-redesign-media__image--match" style={logo ? {backgroundImage: `url(${logo})`} : null}>
                            <span className={`home-redesign-badge ${String(match.status || '').toLowerCase() === 'full' ? 'home-redesign-badge--full' : 'home-redesign-badge--open'}`}>
                              {String(match.status || '').toLowerCase() === 'full' ? t('OPEN_MATCH_STATUS_FULL') : t('OPEN_MATCH_STATUS_OPEN')}
                            </span>
                            <span className="home-redesign-media__overlay-text">{sportDisplayName(match.sport, t)}</span>
                          </div>
                        </button>
                        <div className="home-redesign-rail-card__body">
                          <div className="home-redesign-rail-card__meta">
                            <strong>{match.partnerName}</strong>
                            <span>{sportDisplayName(match.sport, t)}</span>
                          </div>
                          <div className="home-redesign-rail-card__details">
                            <span>{formatDateLong(match.startDatetime, locale)}</span>
                            <span>{formatTime(match.startDatetime)} · {formatTime(match.endDatetime)}</span>
                            <span>{match.playersJoined}/{match.playersRequired} {t('OPEN_MATCH_PLAYERS_SHORT')}</span>
                            {match.minLevel || match.maxLevel || match.levelRangeMin || match.levelRangeMax ? <span>{t('OPEN_MATCH_LEVEL_RANGE', {min: Number(match.minLevel || match.levelRangeMin || 0).toFixed(1), max: Number(match.maxLevel || match.levelRangeMax || 0).toFixed(1)})}</span> : null}
                          </div>
                          <div className="home-redesign-rail-card__actions">
                            <Button type="button" className="btn home-skywork-action home-skywork-action--primary" onClick={openMatch(match)}>
                              {t('HOME_REDESIGN_JOIN_MATCH')}
                            </Button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>

              <section className="home-redesign-section">
                {sectionHeader('05', {
                  primary: t('HOME_REDESIGN_BOOK_TITLE_PRIMARY'),
                  accent: t('HOME_REDESIGN_BOOK_TITLE_ACCENT')
                }, t('HOME_REDESIGN_BOOK_DESC'), (
                  <Button type="button" className="btn home-skywork-action home-skywork-action--secondary home-redesign-section-link" onClick={goto('/player-show-sport-centers?bookable=true')}>
                    {t('HOME_REDESIGN_VIEW_ALL')} <FaChevronRight aria-hidden="true"/>
                  </Button>
                ))}
                <article className="home-redesign-feature-cta home-redesign-feature-cta--book">
                  <div className="home-redesign-feature-cta__icon"><FaCalendarPlus aria-hidden="true"/></div>
                  <div className="home-redesign-feature-cta__copy">
                    <strong>{t('HOME_REDESIGN_BOOK_FEATURE_TITLE')}</strong>
                    <p>{t('HOME_REDESIGN_BOOK_FEATURE_DESC')}</p>
                      <div className="home-redesign-feature-cta__actions">
                        <Button type="button" className="btn home-skywork-action home-skywork-action--primary" onClick={goto('/player-show-sport-centers?bookable=true')}>
                          <FaCalendarPlus aria-hidden="true"/>
                          {t('HOME_REDESIGN_BOOK')}
                        </Button>
                        <Button type="button" className="btn home-skywork-action home-skywork-action--secondary" onClick={goto('/player-bookings')}>
                          <FaCalendarAlt aria-hidden="true"/>
                          {t('PLAYER_HOME_YOUR_RESERVATIONS')}
                        </Button>
                      </div>
                  </div>
                </article>
                {upcomingBookings.length > 0 ? rail(upcomingBookings.map(booking => (
                    <article key={booking.bookingId} className="home-redesign-rail-card home-redesign-rail-card--booking">
                      <div className="home-redesign-booking-card">
                        <div className="home-redesign-booking-card__icon">
                          {bookingPartnerLogoUrl(booking, partners) ? (
                            <img src={bookingPartnerLogoUrl(booking, partners)} alt="" aria-hidden="true"/>
                          ) : (
                            <FaCalendarAlt aria-hidden="true"/>
                          )}
                        </div>
                      <div className="home-redesign-booking-card__body">
                        <strong>{booking.partnerName}</strong>
                        <span>{booking.fieldName || booking.serviceName}</span>
                        <small>{formatDateLong(booking.startDatetime, locale)} · {formatTime(booking.startDatetime)} - {formatTime(booking.endDatetime)}</small>
                      </div>
                    </div>
                  </article>
                ))) : (
                  <div className="home-redesign-empty-strip home-redesign-empty-strip--compact">
                    <FaCalendarAlt aria-hidden="true"/>
                    <p>{t('PLAYER_HOME_NO_RESERVATION_TITLE')}</p>
                  </div>
                )}
              </section>

              <section className="home-redesign-section">
                {sectionHeader('06', {
                  primary: t('HOME_REDESIGN_CENTERS_TITLE_PRIMARY'),
                  accent: t('HOME_REDESIGN_CENTERS_TITLE_ACCENT')
                }, t('HOME_REDESIGN_CENTERS_DESC'), (
                  <Button type="button" className="btn home-skywork-action home-skywork-action--secondary home-redesign-section-link" onClick={goto('/player-show-sport-centers')}>
                    {t('HOME_REDESIGN_VIEW_ALL')} <FaChevronRight aria-hidden="true"/>
                  </Button>
                ))}
                {favoriteCentersPreview.length > 0 ? rail(favoriteCentersPreview.map(partner => (
                  <article key={partner.uid} className="home-redesign-rail-card home-redesign-rail-card--center">
                    <button type="button" className="home-redesign-media" onClick={openCenter(partner)}>
                      <div className="home-redesign-media__image home-redesign-media__image--center" style={partner.imagesNames && partner.imagesNames.length > 0 ? {backgroundImage: `url(${Configuration.apiEndPoint}/images/${partner.uid}?load=${partner.imagesNames[0]})`} : null}>
                        <span className="home-redesign-badge home-redesign-badge--center">{partner.isSpecialist ? t('SPORT_SPECIALISTS') : t('SPORT_CENTERS')}</span>
                        {partnerLogoUrl(partner) ? <span className="home-redesign-media__logo home-redesign-media__logo--circle"><img src={partnerLogoUrl(partner)} alt="" aria-hidden="true"/></span> : null}
                      </div>
                    </button>
                    <div className="home-redesign-rail-card__body">
                      <div className="home-redesign-rail-card__meta">
                        <strong>{partner.name}</strong>
                        <span>{partner.city}</span>
                      </div>
                      <div className="home-redesign-rail-card__details">
                        <span>{partner.description || t('PLAYER_HOME_NO_DESCRIPTION_AVAILABLE')}</span>
                        {partner.sports && partner.sports.length > 0 ? <span>{partner.sports.map(s => sportDisplayName(s, t)).join(' · ')}</span> : null}
                      </div>
                      <div className="home-redesign-rail-card__actions">
                        <Button type="button" className="btn home-skywork-action home-skywork-action--secondary" onClick={openCenter(partner)}>
                          <FaHome aria-hidden="true"/>
                          {t('HOME_REDESIGN_OPEN_CENTER')}
                        </Button>
                        <Button
                          type="button"
                          className={`btn home-skywork-action ${canBookPartner(partner) ? 'home-skywork-action--primary' : 'home-skywork-action--secondary'}`}
                          onClick={showCalendar(partner, null, Boolean(canBookPartner(partner)))}
                        >
                          {canBookPartner(partner) ? <FaCalendarPlus aria-hidden="true"/> : <FaCompass aria-hidden="true"/>}
                          {canBookPartner(partner) ? t('HOME_REDESIGN_BOOK') : t('CLUB_SKYWORK_PLANNER_OPEN')}
                        </Button>
                      </div>
                    </div>
                  </article>
                ))) : (
                  <div className="home-redesign-empty-strip">
                    <FaStar aria-hidden="true"/>
                    <p>{t('PLAYER_HOME_ADD_TO_FAVORITES_DESC')}</p>
                    <Button type="button" className="btn home-skywork-action home-skywork-action--secondary" onClick={goto('/player-show-sport-centers')}>
                      {t('PLAYER_HOME_FIND_FAVORITE_CENTERS')}
                    </Button>
                  </div>
                )}
              </section>

              <section className="home-redesign-section" id="special-events">
                {sectionHeader('07', {
                  primary: t('HOME_REDESIGN_EVENTS_TITLE_PRIMARY'),
                  accent: t('HOME_REDESIGN_EVENTS_TITLE_ACCENT')
                }, t('HOME_REDESIGN_EVENTS_DESC'))}
                {events && events.length > 0 ? rail(events.slice(0, 6).map(eventItem => (
                  <SpecialEventHomeCard
                    key={eventItem.id}
                    event={eventItem}
                    partner={partners.find(partner => Number(partner.uid) === Number(eventItem.uid))}
                    subscriptions={subscriptions}
                    locale={locale}
                    onAccess={openSpecialEventAccess}
                    t={t}
                  />
                ))) : (
                  <div className="home-redesign-empty-strip">
                    <FaCompass aria-hidden="true"/>
                    <p>{t('NO_SPECIAL_EVENTS')}</p>
                  </div>
                )}
              </section>

            </div>
      </div>
      {selectedPermanentVideo === null ? null : <PlayerHighLight video={selectedPermanentVideo}/>}
      <PlayerNewSubscription/>
      <PlayerNewEventSubscription/>
      <PlayerInsufficientFunds/>
    </Fragment>
  );
};

HomeRedesign.propTypes = {
  location: PropTypes.object,
  token: PropTypes.string,
  profile: PropTypes.object,
  partners: PropTypes.array,
  favouritePartners: PropTypes.array,
  favouriteRtvChannels: PropTypes.array,
  bookings: PropTypes.array,
  video: PropTypes.array,
  videoStreaming: PropTypes.array,
  liveVideos: PropTypes.array,
  events: PropTypes.array,
  subscriptions: PropTypes.object,
  selectedPermanentVideo: PropTypes.object,
  loadPartners: PropTypes.func.isRequired,
  loadBookings: PropTypes.func.isRequired,
  loadVideo: PropTypes.func.isRequired,
  loadVideoStreaming: PropTypes.func.isRequired,
  loadLiveVideos: PropTypes.func.isRequired,
  showPartner: PropTypes.func.isRequired,
  showCalendar: PropTypes.func.isRequired,
  goto: PropTypes.func.isRequired,
  sendUsageData: PropTypes.func.isRequired,
  createPurchasedHighlight: PropTypes.func.isRequired,
  copyPurchasedVideo: PropTypes.func.isRequired,
  openSpecialEventAccess: PropTypes.func.isRequired
};

HomeRedesign.defaultProps = {
  location: null,
  token: null,
  profile: null,
  partners: [],
  favouritePartners: [],
  favouriteRtvChannels: [],
  bookings: [],
  video: [],
  videoStreaming: [],
  liveVideos: [],
  events: [],
  subscriptions: null,
  selectedPermanentVideo: null
};

const mapStateToProps = ({login: {token, profile}, player: {partners, favouritePartners, favouriteRtvChannels, bookings, video, videoStreaming, liveVideos, events, subscriptions, selectedPermanentVideo}}) => ({
  token,
  profile,
  partners,
  favouritePartners,
  favouriteRtvChannels,
  bookings,
  video,
  videoStreaming,
  liveVideos,
  events,
  subscriptions,
  selectedPermanentVideo
});

const mapDispatchToProps = dispatch => ({
  loadPartners: () => dispatch(getPartners()),
  loadBookings: () => dispatch(getBookings()),
  loadVideo: () => dispatch(getVideo()),
  loadVideoStreaming: () => dispatch(getVideoStreaming()),
  loadLiveVideos: () => dispatch(getLiveVideos()),
  showPartner: partner => () => {
    dispatch(partnerSelected(partner));
    dispatch(getPartnerFields(partner.uid));
    dispatch(getPartnerServices(partner.uid));
    dispatch(getPartnerEvents(partner.uid));
    if (!partner.isSpecialist) dispatch(push('/show-partner'));
    else dispatch(push('/show-partner-specialist'));
  },
  showCalendar: (partner, fieldId = null, filterBookable = false) => () => {
    dispatch(partnerSelected(partner));
    dispatch(getPartnerFields(partner.uid));
    dispatch(getPartnerServices(partner.uid));
    dispatch(filterBookingFieldId(fieldId));
    dispatch(filterBookableFields(filterBookable));
    dispatch(getSlots(partner.uid, fieldId));
    dispatch(push(filterBookable ? '/player-booking-calendar' : '/player-calendars'));
  },
  goto: path => () => {
    dispatch(push(path));
  },
  sendUsageData: (partnerId, slotId, streaming) => {
    dispatch(postUsageData(1, partnerId, null, slotId, streaming, 0));
  },
  createPurchasedHighlight: videoItem => {
    dispatch(permanentVideoSelected({
      videoId: null,
      slotId: videoItem.slotId,
      sliceHighlight: true,
      name: videoItem.description,
      token: videoItem.token,
      cameras: videoItem.cameras || []
    }));
    dispatch(showVideoHighlightModal());
  },
  copyPurchasedVideo: async videoItem => {
    const response = await dispatch(addVideoToPlayerArea(videoItem.slotId));
    if (response && (response.status === 201 || (response.json && response.json.error === 'video_already_there'))) {
      dispatch(getVideo());
      dispatch(getVideoStreaming());
    }
    return response;
  },
  openSpecialEventAccess: (event, partner, context) => {
    if (!partner || !context.channel) {
      dispatch(showMessage('RTV_CHANNELS', 'NO_RTV_EVENT_CHANNEL'));
      return;
    }

    dispatch(partnerSelected(partner));
    dispatch(rtvChannelSelected(context.channel));
    dispatch(specialEventSelected(event));

    if (context.canWatch) {
      if (Number(context.channel.type) === 1) {
        dispatch(getRtvPartnerVideos(partner.uid, context.channel.id));
        dispatch(push('/player-replayer-tv-video-partner'));
      } else {
        dispatch(getRtvVideos(partner.uid, context.channel.id, event.id));
        dispatch(push('/player-replayer-tv'));
      }
      return;
    }

    if (context.subscriptionStatus.status === subscriptionStatuses.canRequestEvent) {
      dispatch(showPlayerNewEventSubscriptionModal(event));
      return;
    }
    if (context.subscriptionStatus.status === subscriptionStatuses.scheduledEvent) {
      dispatch(showMessage('SUBSCRIPTION', 'SPECIAL_EVENT_ACCESS_SCHEDULED'));
      return;
    }
    if (context.subscriptionStatus.status === subscriptionStatuses.expired) {
      dispatch(showMessage('SUBSCRIPTION', 'SPECIAL_EVENT_ENDED'));
      return;
    }
    dispatch(showPlayerNewSubscriptionModal());
  }
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(HomeRedesign));
